/**
 * Legacy Shopify storefront / customer-account path detection.
 *
 * If a customer somehow lands on our SPA at one of these paths (typically
 * because Shopify Admin → Customer accounts → Storefront URL is set to
 * `https://zooi.se` but Shopify still appended a legacy path), we redirect
 * them to the right place inside zooi.se instead of rendering a 404.
 */

const ORDER_PARAM_KEYS = ["order", "order_id", "order_number", "name"] as const;

/**
 * Exact paths or path-prefixes we consider "legacy Shopify return paths".
 * Each entry MUST start with "/" and MUST NOT cause a redirect loop because
 * the targets all live under different SPA routes.
 */
export const LEGACY_PATH_RULES: ReadonlyArray<{
  match: RegExp;
  /** Either a static target or a fn that gets the original URL. */
  target: string | ((url: URL) => string);
  reason: string;
}> = [
  {
    match: /^\/account\/orders\/[^/]+\/?$/i,
    target: (url) => `/order-bekraftelse${rebuildOrderQuery(url)}`,
    reason: "shopify_account_order_detail",
  },
  {
    match: /^\/account\/orders\/?$/i,
    target: "/mitt-konto",
    reason: "shopify_account_orders_list",
  },
  {
    match: /^\/account\/?$/i,
    target: "/mitt-konto",
    reason: "shopify_account_root",
  },
  {
    match: /^\/orders\/[^/]+\/?$/i,
    target: (url) => `/order-bekraftelse${rebuildOrderQuery(url)}`,
    reason: "shopify_order_token",
  },
  {
    match: /^\/order-status\/?$/i,
    target: (url) => `/order-bekraftelse${rebuildOrderQuery(url)}`,
    reason: "shopify_order_status",
  },
  {
    match: /^\/thank[_-]?you\/?$/i,
    target: (url) => `/order-bekraftelse${rebuildOrderQuery(url)}`,
    reason: "shopify_thank_you",
  },
];

function rebuildOrderQuery(url: URL): string {
  const out = new URLSearchParams();
  for (const k of ORDER_PARAM_KEYS) {
    const v = url.searchParams.get(k);
    if (v) {
      out.set("order", v);
      break;
    }
  }
  // Try to pull a token from /orders/<token> or /account/orders/<token>
  const m = url.pathname.match(/\/orders\/([^/]+)\/?$/i);
  if (!out.get("order") && m && m[1] && /^[A-Za-z0-9._-]{4,64}$/.test(m[1])) {
    out.set("order", m[1]);
  }
  out.set("via", "legacy_redirect");
  return `?${out.toString()}`;
}

export interface LegacyMatch {
  reason: string;
  target: string;
}

/** Returns a redirect target if the URL matches a legacy rule, else null. */
export function matchLegacyShopifyPath(input: URL | string): LegacyMatch | null {
  let url: URL;
  try {
    url = typeof input === "string" ? new URL(input, "https://zooi.se") : input;
  } catch {
    return null;
  }
  for (const rule of LEGACY_PATH_RULES) {
    if (rule.match.test(url.pathname)) {
      const target = typeof rule.target === "function" ? rule.target(url) : rule.target;
      return { reason: rule.reason, target };
    }
  }
  return null;
}

/** Hosts that count as "old Shopify storefront" for monitoring purposes. */
export const LEGACY_SHOPIFY_HOSTS = [
  /\.myshopify\.com$/i,
  /^shop\.app$/i,
  /\.shop\.app$/i,
];

export function isLegacyShopifyHost(hostname: string): boolean {
  return LEGACY_SHOPIFY_HOSTS.some((re) => re.test(hostname));
}

/** Hosts that ARE allowed for checkout itself. */
export const ALLOWED_CHECKOUT_HOSTS = [
  /^checkout\.zooi\.se$/i,
  /^pay\.shopify\.com$/i,
  /^shop\.app$/i, // Shop Pay redirect host — only valid mid-checkout
];

export function isAllowedCheckoutHost(hostname: string): boolean {
  return ALLOWED_CHECKOUT_HOSTS.some((re) => re.test(hostname));
}
